import { useCallback, useMemo } from 'react';
import { Printer } from 'lucide-react';
import {
  bookedInvoicesIn,
  computeBwaReport,
  computeFinanceKpis,
  computeReceivablesAging,
  computeVatReport,
  invoiceSign,
  invoiceSourceLabels,
  type FinanceGateway,
  type IncomingInvoice,
  type IncomingInvoiceGateway,
  type Invoice,
} from '@forq/finance-data';
import { financeRangeOf, todayIso, type ResolvedPeriod } from './period';
import { openBwaDocument, renderBwaHtml } from './bwaDocument';
import { ReportActions, type SectionReport } from './report';
import { CustomizableMetrics } from '@forq/ui';
import { Bars, euro0, Kpi, KpiGrid, num, Panel, pct, SectionShell, useAsyncData } from './shared';
import { warengruppeForInvoiceLine, warengruppeLabels } from './warengruppe';

interface BwaRow { readonly label: string; readonly value: number; readonly emphasis?: boolean; readonly hint?: string; readonly incomplete?: boolean; }

/** Volle Finanz-Auswertung: BWA, USt-VA, Fälligkeiten, Umsatz nach Quelle -- alles zum Zeitraum. */
export function FinanceSection({ tenantId, financeGateway, incomingGateway, period, tenantName }: {
  readonly tenantId: string;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly period: ResolvedPeriod;
  readonly tenantName: string;
}) {
  const load = useCallback(async (): Promise<{ invoices: readonly Invoice[]; incoming: readonly IncomingInvoice[] }> => {
    const [invoices, incoming] = await Promise.all([
      financeGateway.listInvoices({ tenantId }),
      incomingGateway.listIncomingInvoices({ tenantId }),
    ]);
    return { invoices, incoming };
  }, [financeGateway, incomingGateway, tenantId]);
  const { data, loading, error, reload } = useAsyncData(load);

  const model = useMemo(() => {
    if (!data) return null;
    const range = financeRangeOf(period);
    const bwa = computeBwaReport(data.invoices, data.incoming, range);
    const kpis = computeFinanceKpis(data.invoices, data.incoming, range);
    const vat = computeVatReport(data.invoices, data.incoming, range);
    const aging = computeReceivablesAging(data.invoices, todayIso());
    // Umsatz nach Warengruppe: die ECHTE zugeordnete Warengruppe der Zeile bevorzugen (Stufe 3),
    // sonst grob aus der Positionsart ableiten. Gutschriften negativ.
    const wgMap = new Map<string, { net: number; count: number }>();
    for (const invoice of bookedInvoicesIn(data.invoices, range)) {
      const sign = invoiceSign(invoice);
      for (const line of invoice.lines) {
        const label = line.warengruppeName?.trim() || warengruppeLabels[warengruppeForInvoiceLine(line.kind)];
        const bucket = wgMap.get(label) ?? { net: 0, count: 0 };
        bucket.net += line.netAmount * sign;
        bucket.count += 1;
        wgMap.set(label, bucket);
      }
    }
    const byWarengruppe = [...wgMap.entries()]
      .map(([label, value]) => ({ label, net: value.net, count: value.count }))
      .sort((left, right) => right.net - left.net);
    // Umsatz nach Kostenstelle: je Beleg über die (denormalisierte) Kostenstelle gruppiert; ohne = eigener Topf.
    const ccMap = new Map<string, { net: number; count: number }>();
    for (const invoice of bookedInvoicesIn(data.invoices, range)) {
      const key = invoice.costCenterName ?? 'Ohne Kostenstelle';
      const bucket = ccMap.get(key) ?? { net: 0, count: 0 };
      bucket.net += invoice.netTotal * invoiceSign(invoice);
      bucket.count += 1;
      ccMap.set(key, bucket);
    }
    const byCostCenter = [...ccMap.entries()]
      .map(([label, value]) => ({ label, net: value.net, count: value.count }))
      .sort((left, right) => right.net - left.net);
    const bwaRows: BwaRow[] = [
      { label: 'Umsatzerlöse', value: bwa.revenue },
      { label: 'Erlösschmälerungen (Gutschriften)', value: -bwa.creditNotes },
      { label: 'Gesamtleistung', value: bwa.netRevenue, emphasis: true },
      { label: 'Wareneinsatz / Fremdleistungen', value: -bwa.materialExpense, hint: 'aus der Kontierung der Eingangsrechnungen' },
      { label: 'Rohertrag', value: bwa.grossProfit, emphasis: true, hint: `Quote ${pct(bwa.grossProfitRate)}` },
      ...bwa.costBlocks.filter((block) => block.key !== 'material').map((block) => ({
        label: block.label, value: -block.amount,
        hint: block.incompleteFromInvoices ? `Unvollständig: ${block.source}` : `${block.count} Belege`,
        incomplete: block.incompleteFromInvoices,
      })),
      { label: 'Zwischenergebnis (vor Personal und Abschreibungen)', value: bwa.interimResult, emphasis: true },
    ];
    const agingTotal = aging.reduce((sum, bucket) => sum + bucket.amount, 0);
    return { bwa, kpis, vat, aging, bwaRows, agingTotal, byWarengruppe, byCostCenter };
  }, [data, period]);

  const report: SectionReport | null = useMemo(() => {
    if (!model) return null;
    return {
      title: 'Finanzen',
      kpis: [
        { label: 'Gesamtleistung', value: euro0(model.bwa.netRevenue) },
        { label: 'Wareneinsatz', value: euro0(model.bwa.materialExpense) },
        { label: 'Rohertrag', value: euro0(model.bwa.grossProfit) },
        { label: 'Rohertragsquote', value: model.bwa.netRevenue > 0 ? pct(model.bwa.grossProfitRate) : '–' },
        { label: 'Offene Forderungen', value: euro0(model.kpis.receivablesOpen) },
        { label: 'Offene Verbindlichkeiten', value: euro0(model.kpis.payablesOpen) },
        { label: 'USt-Zahllast (vorbereitend)', value: euro0(model.vat.payable) },
      ],
      blocks: [
        { heading: 'Umsatz nach Warengruppe', columns: ['Warengruppe', 'Belege', 'Umsatz netto'], rows: model.byWarengruppe.map((r) => [r.label, r.count, Math.round(r.net)]) },
        { heading: 'Umsatz nach Kostenstelle', columns: ['Kostenstelle', 'Belege', 'Umsatz netto'], rows: model.byCostCenter.map((r) => [r.label, r.count, Math.round(r.net)]) },
        { heading: 'BWA (bis Zwischenergebnis)', columns: ['Position', 'Betrag'], rows: model.bwaRows.map((r) => [r.label, Math.round(r.value)]) },
        { heading: 'Umsatzsteuer nach Satz', columns: ['Steuersatz', 'Netto', 'USt'], rows: model.vat.revenueByRate.map((r) => [`${r.rate} %`, Math.round(r.net), Math.round(r.tax)]) },
        { heading: 'Fälligkeiten Forderungen', columns: ['Altersklasse', 'Belege', 'Offen'], rows: model.aging.map((b) => [b.label, b.count, Math.round(b.amount)]) },
        { heading: 'Umsatz nach Quelle', columns: ['Quelle', 'Belege', 'Umsatz netto'], rows: model.bwa.revenueBySource.map((r) => [invoiceSourceLabels[r.source], r.count, Math.round(r.amount)]) },
      ],
    };
  }, [model]);

  return (
    <SectionShell loading={loading} error={error} onReload={reload}>
      {model && report ? (
        <div className="auswertung__stack">
          <div className="auswertung__sectionhead">
            <span className="sales-eyebrow">Finanzen · {period.label}</span>
            <div className="auswertung__actions">
              <button type="button" className="sales-secondary" onClick={() => openBwaDocument(renderBwaHtml(tenantName, period.label, model.bwa))}>
                <Printer size={15} /> BWA drucken
              </button>
              <ReportActions report={report} period={period} tenantName={tenantName} />
            </div>
          </div>

          <KpiGrid>
            <CustomizableMetrics
              pageKey="auswertung-finanzen"
              tiles={[
                { key: 'gesamtleistung', node: <Kpi label="Gesamtleistung" value={euro0(model.bwa.netRevenue)} hint={`Rohertrag ${euro0(model.bwa.grossProfit)}`} /> },
                { key: 'rohertragsquote', node: <Kpi label="Rohertragsquote" value={model.bwa.netRevenue > 0 ? pct(model.bwa.grossProfitRate) : '–'} hint="netto nach Wareneinsatz" /> },
                { key: 'forderungen', node: <Kpi label="Offene Forderungen" value={euro0(model.kpis.receivablesOpen)} hint={`${num(model.kpis.receivablesCount)} Debitoren`} /> },
                { key: 'verbindlichkeiten', node: <Kpi label="Offene Verbindlichkeiten" value={euro0(model.kpis.payablesOpen)} hint={`${num(model.kpis.payablesCount)} Kreditoren`} /> },
                { key: 'ust', node: <Kpi label="USt-Zahllast (vorb.)" value={euro0(model.vat.payable)} hint="Umsatzsteuer − Vorsteuer" /> },
              ]}
            />
          </KpiGrid>

          <div className="sales-controlling__grid">
            <Panel title="BWA (bis Zwischenergebnis)">
              <table className="sales-controlling__table">
                <tbody>
                  {model.bwaRows.map((row) => (
                    <tr key={row.label} className={row.emphasis ? 'is-sum' : undefined}>
                      <td>{row.emphasis ? <strong>{row.label}</strong> : row.label}{row.hint ? <small style={{ display: 'block', color: 'var(--forq-text-muted)' }}>{row.hint}</small> : null}</td>
                      <td className="is-num">{row.emphasis ? <strong>{euro0(row.value)}</strong> : euro0(row.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="sales-controlling__hint">Kein Betriebsergebnis: Löhne und Abschreibungen fehlen (keine Rechnungen) – die Rechnung endet ehrlich beim Zwischenergebnis.</p>
            </Panel>

            <Panel title="Fälligkeiten Forderungen">
              <table className="sales-controlling__table">
                <thead><tr><th>Altersklasse</th><th className="is-num">Belege</th><th className="is-num">Offen</th><th className="is-num">Anteil</th></tr></thead>
                <tbody>
                  {model.aging.map((bucket) => (
                    <tr key={bucket.key}>
                      <td>{bucket.label}</td>
                      <td className="is-num">{num(bucket.count)}</td>
                      <td className="is-num">{euro0(bucket.amount)}</td>
                      <td className="is-num">{model.agingTotal === 0 ? '–' : pct((bucket.amount / model.agingTotal) * 100)}</td>
                    </tr>
                  ))}
                  <tr className="is-sum"><td><strong>Summe offen</strong></td><td className="is-num"><strong>{num(model.kpis.receivablesCount)}</strong></td><td className="is-num"><strong>{euro0(model.agingTotal)}</strong></td><td className="is-num">100 %</td></tr>
                </tbody>
              </table>
            </Panel>
          </div>

          <div className="sales-controlling__grid">
            <Panel title="Umsatz nach Warengruppe">
              <Bars
                rows={model.byWarengruppe.map((row) => ({ key: row.label, label: row.label, badge: row.count, value: row.net }))}
                emptyLabel="Im Zeitraum wurde nichts abgerechnet."
              />
            </Panel>

            <Panel title="Umsatz nach Kostenstelle">
              <Bars
                rows={model.byCostCenter.map((row) => ({ key: row.label, label: row.label, badge: row.count, value: row.net }))}
                emptyLabel="Im Zeitraum wurde nichts abgerechnet."
              />
            </Panel>
          </div>

          <Panel title="Umsatz nach Quelle">
            <Bars
              rows={model.bwa.revenueBySource.map((row) => ({ key: row.source, label: invoiceSourceLabels[row.source], badge: row.count, value: row.amount }))}
              emptyLabel="Im Zeitraum wurde nichts abgerechnet."
            />
          </Panel>

          <Panel title="Umsatzsteuer / Vorsteuer (vorbereitend)">
            <table className="sales-controlling__table">
              <thead><tr><th>Steuersatz</th><th className="is-num">Netto</th><th className="is-num">USt</th></tr></thead>
              <tbody>
                {model.vat.revenueByRate.length === 0 ? <tr><td colSpan={3}>Keine Ausgangsbelege im Zeitraum.</td></tr> : model.vat.revenueByRate.map((row) => (
                  <tr key={row.rate}><td>{row.rate} %</td><td className="is-num">{euro0(row.net)}</td><td className="is-num">{euro0(row.tax)}</td></tr>
                ))}
                {model.vat.marginBaseTotal !== 0 && (
                  /* Differenzbesteuerung steht auf keinem Beleg, gehoert aber in die Voranmeldung:
                     Bemessungsgrundlage ist die Marge, die Steuer ist darin enthalten. */
                  <tr title="§ 25a UStG — Bemessungsgrundlage ist die Marge, nicht der Umsatz">
                    <td>Differenzbesteuerung § 25a</td>
                    <td className="is-num">{euro0(model.vat.marginBaseTotal)}</td>
                    <td className="is-num">{euro0(model.vat.marginVatTotal)}</td>
                  </tr>
                )}
                <tr className="is-sum"><td><strong>Umsatzsteuer</strong></td><td className="is-num" /><td className="is-num"><strong>{euro0(model.vat.vatTotal)}</strong></td></tr>
                <tr><td>abziehbare Vorsteuer</td><td className="is-num" /><td className="is-num">{euro0(-model.vat.inputTaxTotal)}</td></tr>
                <tr className="is-sum"><td><strong>{model.vat.payable < 0 ? 'Erstattung' : 'Zahllast'}</strong></td><td className="is-num" /><td className="is-num"><strong>{euro0(Math.abs(model.vat.payable))}</strong></td></tr>
              </tbody>
            </table>
            <p className="sales-controlling__hint">Vorbereitend – die Übermittlung an ELSTER erfolgt außerhalb von FORQ.</p>
          </Panel>
        </div>
      ) : null}
    </SectionShell>
  );
}
